import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  MessageSquare,
  ListChecks,
  Send,
  Plus,
  Check,
  Circle,
  CalendarDays,
} from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import DateField from "../ui/DateField";
import { toast } from "sonner";

// Shared advisor↔client conversation + tasks for one connection. `role` is the
// current user's side ("advisor" | "client"); `counterpart` is a display name.
export default function CollaborationPanel({ connectionId, role, counterpart }) {
  const [tab, setTab] = useState("messages");
  const [messages, setMessages] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    if (!connectionId) return;
    try {
      const [m, t] = await Promise.all([
        api.get(`/connections/${connectionId}/messages`),
        api.get(`/connections/${connectionId}/tasks`),
      ]);
      setMessages(m.data.messages || []);
      setTasks(t.data.tasks || []);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [connectionId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (tab === "messages" && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, tab]);

  const send = async () => {
    const text = body.trim();
    if (!text) return;
    setSending(true);
    try {
      const { data } = await api.post(`/connections/${connectionId}/messages`, {
        body: text,
      });
      setMessages((m) => [...m, data]);
      setBody("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const openTasks = tasks.filter((t) => t.status === "open");
  const doneTasks = tasks.filter((t) => t.status !== "open");

  const toggleTask = async (task) => {
    const next = task.status === "done" ? "open" : "done";
    try {
      const { data } = await api.patch(
        `/connections/${connectionId}/tasks/${task.id}`,
        { status: next },
      );
      setTasks((ts) => ts.map((t) => (t.id === task.id ? data : t)));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="flex flex-col h-[28rem]">
      {/* TABS */}
      <div className="flex gap-1 px-1 border-b border-slate-200 dark:border-slate-800">
        <TabBtn active={tab === "messages"} onClick={() => setTab("messages")} icon={MessageSquare} label="Messages" count={messages.length} />
        <TabBtn active={tab === "tasks"} onClick={() => setTab("tasks")} icon={ListChecks} label="Tasks" count={openTasks.length} />
      </div>

      {tab === "messages" ? (
        <>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2">
            {messages.length === 0 ? (
              <p className="text-center text-sm text-slate-400 mt-8">
                No messages yet. Say hello to {counterpart || "your contact"}.
              </p>
            ) : (
              messages.map((m) => {
                const mine = m.sender_role === role;
                return (
                  <div
                    key={m.id}
                    className={`flex ${mine ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${
                        mine
                          ? "bg-emerald-500 text-white rounded-br-sm"
                          : "bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-100 rounded-bl-sm"
                      }`}
                    >
                      <div className="whitespace-pre-wrap break-words">{m.body}</div>
                      <div className={`mt-0.5 text-[10px] ${mine ? "text-white/70" : "text-slate-400"}`}>
                        {new Date(m.created_at).toLocaleString([], {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <div className="p-2 border-t border-slate-200 dark:border-slate-800 flex items-end gap-2">
            <textarea
              rows={1}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="Write a message…"
              className="input-soft flex-1 resize-none max-h-28"
            />
            <button
              onClick={send}
              disabled={sending || !body.trim()}
              className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </>
      ) : (
        <TasksView
          role={role}
          openTasks={openTasks}
          doneTasks={doneTasks}
          onToggle={toggleTask}
          onAdded={(t) => setTasks((ts) => [t, ...ts])}
          connectionId={connectionId}
        />
      )}
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, count }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
        active
          ? "border-emerald-500 text-emerald-700 dark:text-emerald-300"
          : "border-transparent text-slate-500 hover:text-slate-700"
      }`}
    >
      <Icon className="h-4 w-4" />
      {label}
      {count > 0 && (
        <span className="ml-0.5 grid h-4 min-w-4 px-1 place-items-center rounded-full bg-slate-200 dark:bg-slate-700 text-[10px] font-bold">
          {count}
        </span>
      )}
    </button>
  );
}

function TasksView({ role, openTasks, doneTasks, onToggle, onAdded, connectionId }) {
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [assignedRole, setAssignedRole] = useState(role === "advisor" ? "client" : "advisor");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/connections/${connectionId}/tasks`, {
        title: title.trim(),
        due_date: due || null,
        assigned_role: assignedRole,
      });
      onAdded(data);
      setTitle("");
      setDue("");
      setAdding(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto p-3">
      {!adding ? (
        <button
          onClick={() => setAdding(true)}
          className="w-full mb-3 flex items-center justify-center gap-1.5 rounded-xl border border-dashed border-slate-300 dark:border-slate-700 py-2.5 text-sm font-medium text-slate-500 hover:text-emerald-600 hover:border-emerald-300"
        >
          <Plus className="h-4 w-4" /> Add a task / reminder
        </button>
      ) : (
        <div className="mb-3 rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-2">
          <input
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Task title (e.g. Upload latest payslip)"
            className="input-soft"
          />
          <div className="flex gap-2">
            <DateField
              value={due}
              onChange={(v) => setDue(v)}
              className="flex-1"
            />
            <select
              value={assignedRole}
              onChange={(e) => setAssignedRole(e.target.value)}
              className="input-soft flex-1"
            >
              <option value="client">For the client</option>
              <option value="advisor">For the advisor</option>
            </select>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setAdding(false)} className="btn-secondary !py-1.5 text-sm">
              Cancel
            </button>
            <button
              onClick={add}
              disabled={busy || !title.trim()}
              className="rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold px-3 py-1.5 disabled:opacity-50"
            >
              Add task
            </button>
          </div>
        </div>
      )}

      {openTasks.length === 0 && doneTasks.length === 0 && (
        <p className="text-center text-sm text-slate-400 mt-6">No tasks yet.</p>
      )}

      <div className="space-y-1.5">
        {openTasks.map((t) => (
          <TaskRow key={t.id} task={t} onToggle={onToggle} />
        ))}
      </div>

      {doneTasks.length > 0 && (
        <>
          <div className="mt-4 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            Completed
          </div>
          <div className="space-y-1.5 opacity-60">
            {doneTasks.map((t) => (
              <TaskRow key={t.id} task={t} onToggle={onToggle} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function TaskRow({ task, onToggle }) {
  const done = task.status === "done";
  return (
    <div className="flex items-start gap-2.5 rounded-xl border border-slate-200 dark:border-slate-800 px-3 py-2.5">
      <button onClick={() => onToggle(task)} className="mt-0.5 shrink-0">
        {done ? (
          <Check className="h-4 w-4 text-emerald-500" />
        ) : (
          <Circle className="h-4 w-4 text-slate-300 hover:text-emerald-500" />
        )}
      </button>
      <div className="min-w-0 flex-1">
        <div className={`text-sm font-medium ${done ? "line-through text-slate-400" : "text-slate-800 dark:text-slate-100"}`}>
          {task.title}
        </div>
        <div className="flex items-center gap-2 text-[11px] text-slate-400 mt-0.5">
          <span className="capitalize rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5">
            for {task.assigned_role}
          </span>
          {task.due_date && (
            <span className="inline-flex items-center gap-1">
              <CalendarDays className="h-3 w-3" />
              {new Date(task.due_date).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
